import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Edit,
  Plane,
  Heart,
  Shield,
  Loader2,
  X,
  CheckCircle,
  Upload,
  FileText,
  Eye,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  usePetInsurance,
  type TravelInsurance,
  type HealthInsurance,
} from '../../../../hooks/usePetInsurance';
import { DocumentUploadModal } from '../epassport/DocumentUploadModal';
import { DocumentViewerModal } from './DocumentViewerModal';
import { apiClient } from '@/api/axios';
import { useNotifications } from '../../context/NotificationsContext';
import { usePets } from '../../context/PetsContext';

interface InsuranceRecordsProps {
  petId: string;
  /** When true, used inside E-Passport Travel section - shorter description */
  embedded?: boolean;
}

type EditMode = 'travel' | 'health' | null;
type DocumentUploadTarget = 'travel' | 'health' | null;

export function InsuranceRecords({
  petId,
  embedded: _embedded = false,
}: InsuranceRecordsProps) {
  const { t } = useTranslation();
  const { insurance, updateTravelInsurance, updateHealthInsurance } =
    usePetInsurance(petId);
  const { addNotification } = useNotifications();
  const { pets } = usePets();
  const petName =
    pets.find((p) => p.id === petId)?.name ?? t('common.pet', 'Pet');
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [saving, setSaving] = useState(false);
  const [documentUploadTarget, setDocumentUploadTarget] =
    useState<DocumentUploadTarget>(null);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [viewingDocument, setViewingDocument] = useState<{
    name: string;
    url: string;
  } | null>(null);

  const travel = insurance.travel || {};
  const health = insurance.health || {};
  const hasTravel = Object.values(travel).some((v) => v && String(v).trim());
  const hasHealth = Object.values(health).some((v) => v && String(v).trim());

  const [travelForm, setTravelForm] = useState<TravelInsurance>({
    provider: '',
    policyNumber: '',
    coverageStart: '',
    coverageEnd: '',
    coverageDetails: '',
    emergencyContact: '',
  });

  const [healthForm, setHealthForm] = useState<HealthInsurance>({
    provider: '',
    policyNumber: '',
    coverageType: '',
    coverageStart: '',
    coverageEnd: '',
    deductible: '',
    coverageDetails: '',
  });

  const openTravelEdit = () => {
    setTravelForm({
      provider: travel.provider || '',
      policyNumber: travel.policyNumber || '',
      coverageStart: travel.coverageStart || '',
      coverageEnd: travel.coverageEnd || '',
      coverageDetails: travel.coverageDetails || '',
      emergencyContact: travel.emergencyContact || '',
    });
    setEditMode('travel');
  };

  const openHealthEdit = () => {
    setHealthForm({
      provider: health.provider || '',
      policyNumber: health.policyNumber || '',
      coverageType: health.coverageType || '',
      coverageStart: health.coverageStart || '',
      coverageEnd: health.coverageEnd || '',
      deductible: health.deductible || '',
      coverageDetails: health.coverageDetails || '',
    });
    setEditMode('health');
  };

  const handleSaveTravel = async () => {
    setSaving(true);
    try {
      updateTravelInsurance(travelForm);
      addNotification({
        type: 'record_added',
        title: t(
          'features.healthRecords.recordUpdatedTitle',
          'Record salute aggiornato'
        ),
        message: t('features.healthRecords.travelInsuranceUpdatedMessage', {
          petName,
          defaultValue:
            "L'assicurazione viaggio è stata aggiornata per {{petName}}.",
        }),
        petId,
        petName,
        icon: '✈️',
        source: 'E-Passport',
      });
      toast.success(
        t(
          'features.healthRecords.travelInsuranceUpdated',
          'Assicurazione viaggio aggiornata'
        )
      );
      setEditMode(null);
    } catch {
      toast.error(
        t(
          'features.healthRecords.saveFailed',
          'Salvataggio non riuscito. Riprova.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHealth = async () => {
    setSaving(true);
    try {
      updateHealthInsurance(healthForm);
      addNotification({
        type: 'record_added',
        title: t(
          'features.healthRecords.recordUpdatedTitle',
          'Record salute aggiornato'
        ),
        message: t('features.healthRecords.healthInsuranceUpdatedMessage', {
          petName,
          defaultValue:
            "L'assicurazione salute è stata aggiornata per {{petName}}.",
        }),
        petId,
        petName,
        icon: '❤️',
        source: 'E-Passport',
      });
      toast.success(
        t(
          'features.healthRecords.healthInsuranceUpdated',
          'Assicurazione salute aggiornata'
        )
      );
      setEditMode(null);
    } catch {
      toast.error(
        t(
          'features.healthRecords.saveFailed',
          'Salvataggio non riuscito. Riprova.'
        )
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUploadComplete = (
    type: 'travel' | 'health',
    file: { name: string; size: string; type: string; url: string }
  ) => {
    const doc = {
      name: file.name,
      url: file.url,
      size: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString(),
    };
    if (type === 'travel') {
      updateTravelInsurance({ document: doc });
    } else {
      updateHealthInsurance({ document: doc });
    }
    addNotification({
      type: 'document_shared',
      title: t('documents.documentUploaded', 'Documento caricato'),
      message: t('features.healthRecords.insuranceDocumentAddedMessage', {
        type:
          type === 'travel'
            ? t('features.healthRecords.travelInsurance')
            : t('features.healthRecords.healthInsurance'),
        petName,
        defaultValue:
          'Documento assicurativo {{type}} aggiunto per {{petName}}.',
      }),
      petId,
      petName,
      icon: '📄',
      source: 'E-Passport',
    });
    setDocumentUploadTarget(null);
  };

  const handleDocumentRemove = (type: 'travel' | 'health') => {
    if (type === 'travel') {
      updateTravelInsurance({ document: undefined });
    } else {
      updateHealthInsurance({ document: undefined });
    }
    toast.success(t('documents.removeDocument', 'Documento rimosso'));
  };

  const handleDocumentDownload = async (doc: { name: string; url: string }) => {
    try {
      toast.success(
        t('documents.downloadStarted', {
          defaultValue: `Download di ${doc.name} avviato...`,
        })
      );
      const encodedKey = encodeURIComponent(doc.url);
      const res = await apiClient.get<{ url: string; filename?: string }>(
        `/documents/${encodedKey}/download`,
        { params: { disposition: 'attachment' } }
      );
      const a = document.createElement('a');
      a.href = res.data.url;
      a.download = res.data.filename || doc.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed', err);
      toast.error(
        t('documents.downloadFailed', 'Download documento non riuscito')
      );
    }
  };

  const DocumentSection = ({
    type,
    document: doc,
    label,
  }: {
    type: 'travel' | 'health';
    document?: {
      name: string;
      url: string;
      size?: string;
      uploadedAt?: string;
    };
    label: string;
  }) => (
    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-white/10">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-500 dark:text-white/50">
          {label} ({t('common.optional', 'Optional')})
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => {
              if (doc?.url) {
                setViewingDocument({ name: doc.name, url: doc.url });
                setShowDocumentViewer(true);
              }
            }}
            disabled={!doc?.url}
            className={`flex items-center gap-1 text-xs ${
              doc?.url
                ? 'text-blue-600 dark:text-blue-400 hover:underline'
                : 'text-gray-400 dark:text-white/30 cursor-not-allowed'
            }`}
          >
            <Eye className="w-3 h-3" />
            {t('features.healthRecords.insuranceDocuments.view', 'View')}
          </button>
          {doc ? (
            <>
              <button
                onClick={() => handleDocumentDownload(doc)}
                className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
              >
                <FileText className="w-3 h-3" />
                {t(
                  'features.healthRecords.insuranceDocuments.download',
                  'Download'
                )}
              </button>
              <button
                onClick={() => setDocumentUploadTarget(type)}
                className="flex items-center gap-1 text-xs text-gray-600 dark:text-white/70 hover:underline"
              >
                {t(
                  'features.healthRecords.insuranceDocuments.replace',
                  'Replace'
                )}
              </button>
              <button
                onClick={() => handleDocumentRemove(type)}
                className="flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                <Trash2 className="w-3 h-3" />
                {t(
                  'features.healthRecords.insuranceDocuments.remove',
                  'Remove'
                )}
              </button>
            </>
          ) : (
            <button
              onClick={() => setDocumentUploadTarget(type)}
              className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              <Upload className="w-3 h-3" />
              {t('features.healthRecords.insuranceDocuments.upload', 'Upload')}
            </button>
          )}
        </div>
      </div>
      {doc && (
        <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-[#0F0E16] rounded-lg">
          <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm truncate text-gray-900 dark:text-white">
              {doc.name}
            </div>
            {doc.size && (
              <div className="text-xs text-gray-500 dark:text-white/50">
                {doc.size}
                {doc.uploadedAt &&
                  ` • ${new Date(doc.uploadedAt).toLocaleDateString()}`}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h3
          className="text-xl mb-1 text-gray-900 dark:text-white"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
        >
          {t('features.healthRecords.insuranceTitle')}
        </h3>
        <p
          className="text-sm text-gray-600 dark:text-white/70"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t('features.healthRecords.insuranceDesc')}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Travel Insurance Card */}
        <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h4
                className="text-lg text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.healthRecords.travelInsurance')}
              </h4>
            </div>
            <button
              onClick={openTravelEdit}
              className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {hasTravel ? (
                <>
                  <Edit className="w-4 h-4" />
                  {t('common.edit')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {t('features.healthRecords.addDetails')}
                </>
              )}
            </button>
          </div>

          {hasTravel ? (
            <div className="space-y-3 text-sm">
              {travel.provider && (
                <div>
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.provider')}:
                  </span>{' '}
                  {travel.provider}
                </div>
              )}
              {travel.policyNumber && (
                <div>
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.policyNumber')}:
                  </span>{' '}
                  {travel.policyNumber}
                </div>
              )}
              {(travel.coverageStart || travel.coverageEnd) && (
                <div>
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.coverage')}:
                  </span>{' '}
                  {[travel.coverageStart, travel.coverageEnd]
                    .filter(Boolean)
                    .join(' – ')}
                </div>
              )}
              {travel.emergencyContact && (
                <div>
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.emergency')}:
                  </span>{' '}
                  {travel.emergencyContact}
                </div>
              )}
              {travel.coverageDetails && (
                <div className="pt-2 border-t border-gray-200 dark:border-white/10">
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.details')}:
                  </span>
                  <p className="mt-1 text-gray-700 dark:text-white/90">
                    {travel.coverageDetails}
                  </p>
                </div>
              )}
              <div className="pt-2">
                <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  {t('features.healthRecords.insuranceFields.recorded')}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-white/50 border-2 border-dashed border-gray-300 dark:border-white/10 rounded-lg">
              <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm mb-2">
                {t('features.healthRecords.noTravelInsuranceYet')}
              </p>
              <button
                onClick={openTravelEdit}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                {t('features.healthRecords.addDetails')}
              </button>
            </div>
          )}
          <DocumentSection
            type="travel"
            document={travel.document}
            label={t(
              'features.healthRecords.insuranceDocuments.travelDocument',
              'Travel insurance document (optional)'
            )}
          />
        </div>

        {/* Health Insurance Card */}
        <div className="bg-white dark:bg-[#0F0E16] border border-gray-200 dark:border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-green-600 dark:text-green-400" />
              <h4
                className="text-lg text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
              >
                {t('features.healthRecords.healthInsurance')}
              </h4>
            </div>
            <button
              onClick={openHealthEdit}
              className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {hasHealth ? (
                <>
                  <Edit className="w-4 h-4" />
                  {t('common.edit')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  {t('features.healthRecords.addDetails')}
                </>
              )}
            </button>
          </div>

          {hasHealth ? (
            <div className="space-y-3 text-sm">
              {health.provider && (
                <div>
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.provider')}:
                  </span>{' '}
                  {health.provider}
                </div>
              )}
              {health.policyNumber && (
                <div>
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.policyNumber')}:
                  </span>{' '}
                  {health.policyNumber}
                </div>
              )}
              {health.coverageType && (
                <div>
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.type')}:
                  </span>{' '}
                  {health.coverageType}
                </div>
              )}
              {(health.coverageStart || health.coverageEnd) && (
                <div>
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.coverage')}:
                  </span>{' '}
                  {[health.coverageStart, health.coverageEnd]
                    .filter(Boolean)
                    .join(' – ')}
                </div>
              )}
              {health.deductible && (
                <div>
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.deductible')}:
                  </span>{' '}
                  {health.deductible}
                </div>
              )}
              {health.coverageDetails && (
                <div className="pt-2 border-t border-gray-200 dark:border-white/10">
                  <span className="text-gray-500 dark:text-white/50">
                    {t('features.healthRecords.insuranceFields.details')}:
                  </span>
                  <p className="mt-1 text-gray-700 dark:text-white/90">
                    {health.coverageDetails}
                  </p>
                </div>
              )}
              <div className="pt-2">
                <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                  <CheckCircle className="w-4 h-4" />
                  {t('features.healthRecords.insuranceFields.recorded')}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-white/50 border-2 border-dashed border-gray-300 dark:border-white/10 rounded-lg">
              <Shield className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm mb-2">
                {t('features.healthRecords.noHealthInsuranceYet')}
              </p>
              <button
                onClick={openHealthEdit}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
              >
                {t('features.healthRecords.addDetails')}
              </button>
            </div>
          )}
          <DocumentSection
            type="health"
            document={health.document}
            label={t(
              'features.healthRecords.insuranceDocuments.healthDocument',
              'Health insurance document (optional)'
            )}
          />
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
        <h4
          className="mb-2 text-gray-900 dark:text-white"
          style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
        >
          {t('features.healthRecords.whyInsuranceMatters', {
            defaultValue: 'Why insurance details matter',
          })}
        </h4>
        <p
          className="text-sm text-gray-700 dark:text-blue-100"
          style={{ fontFamily: 'Inter, sans-serif' }}
        >
          {t('features.healthRecords.whyInsuranceBody', {
            defaultValue:
              'Clear policy details and documents make emergencies, travel claims, and routine care approvals much faster.',
          })}
        </p>
      </div>

      {/* Travel Insurance Edit Modal */}
      {editMode === 'travel' && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-lg w-full border dark:border-white/8 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-white/8 flex-shrink-0">
              <h3
                className="text-xl text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.healthRecords.insuranceModal.travelTitle')}
              </h3>
              <button
                onClick={() => setEditMode(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white/70"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                  {t('features.healthRecords.insuranceModal.provider')}
                </label>
                <input
                  type="text"
                  value={travelForm.provider}
                  onChange={(e) =>
                    setTravelForm((p) => ({ ...p, provider: e.target.value }))
                  }
                  placeholder={t(
                    'features.healthRecords.insuranceModal.providerTravelPlaceholder'
                  )}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                  {t('features.healthRecords.insuranceModal.policyNumber')}
                </label>
                <input
                  type="text"
                  value={travelForm.policyNumber}
                  onChange={(e) =>
                    setTravelForm((p) => ({
                      ...p,
                      policyNumber: e.target.value,
                    }))
                  }
                  placeholder={t(
                    'features.healthRecords.insuranceModal.policyTravelPlaceholder'
                  )}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                    {t('features.healthRecords.insuranceModal.coverageStart')}
                  </label>
                  <input
                    type="date"
                    value={travelForm.coverageStart}
                    onChange={(e) =>
                      setTravelForm((p) => ({
                        ...p,
                        coverageStart: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                    {t('features.healthRecords.insuranceModal.coverageEnd')}
                  </label>
                  <input
                    type="date"
                    value={travelForm.coverageEnd}
                    onChange={(e) =>
                      setTravelForm((p) => ({
                        ...p,
                        coverageEnd: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                  {t('features.healthRecords.insuranceModal.emergencyContact')}
                </label>
                <input
                  type="text"
                  value={travelForm.emergencyContact}
                  onChange={(e) =>
                    setTravelForm((p) => ({
                      ...p,
                      emergencyContact: e.target.value,
                    }))
                  }
                  placeholder={t(
                    'features.healthRecords.insuranceModal.emergencyContactPlaceholder'
                  )}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                  {t(
                    'features.healthRecords.insuranceModal.coverageDetailsOptional'
                  )}
                </label>
                <textarea
                  value={travelForm.coverageDetails}
                  onChange={(e) =>
                    setTravelForm((p) => ({
                      ...p,
                      coverageDetails: e.target.value,
                    }))
                  }
                  placeholder={t(
                    'features.healthRecords.insuranceModal.coverageDetailsPlaceholder'
                  )}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-white/8 flex-shrink-0">
              <button
                onClick={() => setEditMode(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5"
              >
                {t('features.healthRecords.insuranceModal.cancel')}
              </button>
              <button
                onClick={handleSaveTravel}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('features.healthRecords.insuranceModal.save')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Health Insurance Edit Modal */}
      {editMode === 'health' && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-lg w-full border dark:border-white/8 max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-white/8 flex-shrink-0">
              <h3
                className="text-xl text-gray-900 dark:text-white"
                style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
              >
                {t('features.healthRecords.insuranceModal.healthTitle')}
              </h3>
              <button
                onClick={() => setEditMode(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-white/70"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                  {t('features.healthRecords.insuranceModal.provider')}
                </label>
                <input
                  type="text"
                  value={healthForm.provider}
                  onChange={(e) =>
                    setHealthForm((p) => ({ ...p, provider: e.target.value }))
                  }
                  placeholder={t(
                    'features.healthRecords.insuranceModal.providerHealthPlaceholder'
                  )}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                  {t('features.healthRecords.insuranceModal.policyNumber')}
                </label>
                <input
                  type="text"
                  value={healthForm.policyNumber}
                  onChange={(e) =>
                    setHealthForm((p) => ({
                      ...p,
                      policyNumber: e.target.value,
                    }))
                  }
                  placeholder={t(
                    'features.healthRecords.insuranceModal.policyHealthPlaceholder'
                  )}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                  {t('features.healthRecords.insuranceModal.coverageType')}
                </label>
                <select
                  value={healthForm.coverageType}
                  onChange={(e) =>
                    setHealthForm((p) => ({
                      ...p,
                      coverageType: e.target.value,
                    }))
                  }
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                >
                  <option value="">
                    {t('features.healthRecords.insuranceModal.selectType')}
                  </option>
                  <option value="Accident Only">
                    {t(
                      'features.healthRecords.insuranceModal.typeAccidentOnly'
                    )}
                  </option>
                  <option value="Accident & Illness">
                    {t(
                      'features.healthRecords.insuranceModal.typeAccidentIllness'
                    )}
                  </option>
                  <option value="Wellness">
                    {t('features.healthRecords.insuranceModal.typeWellness')}
                  </option>
                  <option value="Comprehensive">
                    {t(
                      'features.healthRecords.insuranceModal.typeComprehensive'
                    )}
                  </option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                    {t('features.healthRecords.insuranceModal.coverageStart')}
                  </label>
                  <input
                    type="date"
                    value={healthForm.coverageStart}
                    onChange={(e) =>
                      setHealthForm((p) => ({
                        ...p,
                        coverageStart: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                    {t('features.healthRecords.insuranceModal.coverageEnd')}
                  </label>
                  <input
                    type="date"
                    value={healthForm.coverageEnd}
                    onChange={(e) =>
                      setHealthForm((p) => ({
                        ...p,
                        coverageEnd: e.target.value,
                      }))
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                  {t(
                    'features.healthRecords.insuranceModal.deductibleOptional'
                  )}
                </label>
                <input
                  type="text"
                  value={healthForm.deductible}
                  onChange={(e) =>
                    setHealthForm((p) => ({
                      ...p,
                      deductible: e.target.value,
                    }))
                  }
                  placeholder={t(
                    'features.healthRecords.insuranceModal.deductiblePlaceholder'
                  )}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-white/70 mb-1">
                  {t(
                    'features.healthRecords.insuranceModal.coverageDetailsOptional'
                  )}
                </label>
                <textarea
                  value={healthForm.coverageDetails}
                  onChange={(e) =>
                    setHealthForm((p) => ({
                      ...p,
                      coverageDetails: e.target.value,
                    }))
                  }
                  placeholder={t(
                    'features.healthRecords.insuranceModal.coverageDetailsPlaceholder'
                  )}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg dark:bg-[#0F0E16] dark:text-white resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-white/8 flex-shrink-0">
              <button
                onClick={() => setEditMode(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-white/20 rounded-lg hover:bg-gray-50 dark:hover:bg-white/5"
              >
                {t('features.healthRecords.insuranceModal.cancel')}
              </button>
              <button
                onClick={handleSaveHealth}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('features.healthRecords.insuranceModal.save')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {documentUploadTarget && (
        <DocumentUploadModal
          isOpen={!!documentUploadTarget}
          onClose={() => setDocumentUploadTarget(null)}
          documentName={
            documentUploadTarget === 'travel'
              ? t(
                  'features.healthRecords.insuranceDocuments.travelDocument',
                  'Travel insurance document (optional)'
                )
              : t(
                  'features.healthRecords.insuranceDocuments.healthDocument',
                  'Health insurance document (optional)'
                )
          }
          isReplace={
            documentUploadTarget === 'travel'
              ? !!travel.document
              : !!health.document
          }
          folder={`pets/${petId}/insurance/${documentUploadTarget}`}
          onUploadComplete={(file) =>
            handleDocumentUploadComplete(documentUploadTarget!, file)
          }
        />
      )}

      {showDocumentViewer && viewingDocument?.url && (
        <DocumentViewerModal
          onClose={() => {
            setShowDocumentViewer(false);
            setViewingDocument(null);
          }}
          documentHash={viewingDocument.url}
          documentTitle={viewingDocument.name}
        />
      )}
    </div>
  );
}
